import RefundReason from '@/components/RefundRequest/RefundReason.vue'
import RefundRequestStatusButton from '@/components/RefundRequest/RefundRequestStatusButton.vue'
import { HandCoins, Info, Store, User2 } from 'lucide-vue-next'
import { Groups, JSSchema } from 'pejer/src/tables'
import { MoneyFormatter } from 'pejer/src/tables/Global'
import { ColorPalette } from 'pejer/src/tables/helpers'
import { Environment } from 'pejer/src/types/types'
import { h } from 'vue'

export namespace RefundRequestEmbed {
  export interface Full extends ListItem {}

  export interface ListItem {
    id: number
    environment: Environment
    fromBankAccountNumber?: string
    toBankAccountNumber?: string
    amount: number
    shortDescription?: string
    platformID: string
    payoutReason: string
    paymentReferenceID?: string
    paidWithKID?: string
    insertTime: string
    updateTime: string
    isAccepted?: string

    hasRemittanceAgreement: boolean

    connectedAccount: ConnectedAccount
    payment: Payment
    customer: Customer
  }

  export interface Customer {
    customerId: number
    customerGuid: string
    customerType: string
    email: string
    mobile: string
    mobilePrefix: string
    name: string
    updateTime: string
  }

  export interface Payment {
    paymentReferenceID: string
    currency: string
    grossAmount: number
    description: string
    paymentStatus: string
    paymentProvider: string
    environment: Environment
  }

  export interface ConnectedAccount {
    connectedAccountID: string
    name: string | null
    businessName: string
    countryCode: string | null
    business_TaxID: string | null
  }
}

export const RefundRequestEmbedGroups = {
  Beløp: {
    icon: HandCoins,
    color: ColorPalette.red,
    isMainGroup: true,
    mainColumn: 'amount',
  },
  Refunderingsforespørsel: {
    icon: HandCoins,
    color: ColorPalette.green,
    mainColumn: 'payoutReason',
  },
  Selger: {
    icon: Store,
    color: ColorPalette.pink,
    mainColumn: 'connectedAccount.name',
  },
  Kunde: {
    icon: User2,
    color: ColorPalette.blue,
    mainColumn: 'customer.name',
  },
  Status: {
    icon: Info,
    color: ColorPalette.orange,
    mainColumn: 'isAccepted',
  },
} satisfies Groups

export const RefundRequestEmbedListSchema: JSSchema<
  RefundRequestEmbed.ListItem,
  typeof RefundRequestEmbedGroups
> = {
  name: 'RefundRequest Embed',
  title: 'RefundRequest',
  groups: RefundRequestEmbedGroups,
  fields: {
    id: {
      type: 'number',
      humanReadable: 'Forespørsel ID',
      meta: {
        group: 'Refunderingsforespørsel',
        hideFromTable: true,
      },
    },
    amount: {
      type: 'number',
      humanReadable: 'Beløp',
      meta: {
        group: 'Beløp',
        isMain: true,
      },
      formatter: MoneyFormatter,
      metaType: 'money',
      inSearchSelect: true,
    },
    toBankAccountNumber: {
      type: 'string',
      humanReadable: 'Kundens kontonummer',
      meta: {
        group: 'Beløp',
        hideHeaderInCell: false,
      },
    },
    fromBankAccountNumber: {
      type: 'string',
      humanReadable: 'Selgers kontonummer',
      meta: {
        group: 'Refunderingsforespørsel',
      },
    },
    shortDescription: {
      type: 'string',
      humanReadable: 'Beskrivelse',
      meta: {
        group: 'Beløp',
      },
    },
    platformID: {
      type: 'string',
      humanReadable: 'Plattform-ID',
      meta: {
        group: 'Refunderingsforespørsel',
        hideFromTable: true,
      },
    },
    payoutReason: {
      type: 'string',
      humanReadable: 'Refunderingsårsak',
      meta: {
        isMain: true,
        group: 'Refunderingsforespørsel',
      },
      formattedCell: (value) =>
        h(RefundReason, { payoutReason: value as RefundRequestEmbed.ListItem['payoutReason'] }),
    },
    insertTime: {
      type: 'date',
      humanReadable: 'Dato for forespørsel',
      meta: {
        group: 'Refunderingsforespørsel',
      },
      isDefaultSortColumn: true,
    },

    paidWithKID: {
      type: 'string',
      humanReadable: 'Betalt med KID',
      meta: {
        group: 'Refunderingsforespørsel',
      },
    },

    isAccepted: {
      type: 'string',
      humanReadable: 'Status',
      meta: {
        group: 'Status',
        hideActions: true,
      },
      specificCell:
        (self) =>
        ({ row }) => {
          return h(RefundRequestStatusButton, {
            refundRequestItem: row.original,
          })
        },
    },

    hasRemittanceAgreement: {
      type: 'boolean',
      humanReadable: 'Har remitteringsavtale',
      meta: {
        group: 'Status',
        hideFromTable: true,
      },
    },

    payment: {
      type: 'object',
      humanReadable: 'Refunderingsforespørsel',
      meta: {
        group: 'Refunderingsforespørsel',
      },
      values: {
        paymentReferenceID: {
          type: 'string',
          humanReadable: 'Betalingsreferanse',
          meta: {
            group: 'Refunderingsforespørsel',
            hideFromTable: true,
          },
        },
        currency: {
          type: 'string',
          humanReadable: 'Valuta',
          meta: {
            group: 'Refunderingsforespørsel',
          },
        },
        grossAmount: {
          type: 'number',
          humanReadable: 'Opprinnelig beløp',
          meta: {
            group: 'Refunderingsforespørsel',
          },
          formatter: MoneyFormatter,
        },
        description: {
          type: 'string',
          humanReadable: 'Beskrivelse',
          meta: {
            group: 'Refunderingsforespørsel',
          },
        },
        paymentStatus: {
          type: 'string',
          humanReadable: 'Betalingsstatus',
          meta: {
            group: 'Refunderingsforespørsel',
          },
        },
        paymentProvider: {
          type: 'string',
          humanReadable: 'Betalingsleverandør',
          meta: {
            group: 'Refunderingsforespørsel',
          },
        },
        environment: {
          type: 'string',
          humanReadable: 'Miljø',
          meta: {
            group: 'Refunderingsforespørsel',
            hideFromTable: true,
          },
        },
      },
    },

    customer: {
      type: 'object',
      humanReadable: 'Kunde',
      meta: {
        group: 'Kunde',
      },
      values: {
        customerId: {
          type: 'number',
          humanReadable: 'Kunde-ID',
          meta: {
            group: 'Kunde',
            hideFromTable: true,
          },
        },
        customerGuid: {
          type: 'string',
          humanReadable: 'Kunde-GUID',
          meta: {
            group: 'Kunde',
            hideFromTable: true,
          },
        },
        customerType: {
          type: 'string',
          humanReadable: 'Kundetype',
          meta: {
            group: 'Kunde',
          },
        },
        email: {
          type: 'string',
          humanReadable: 'E-post',
          meta: {
            group: 'Kunde',
          },
        },
        mobile: {
          type: 'string',
          humanReadable: 'Mobilnummer',
          meta: {
            group: 'Kunde',
          },
        },
        mobilePrefix: {
          type: 'string',
          humanReadable: 'Mobilnummer prefiks',
          meta: {
            group: 'Kunde',
          },
        },
        name: {
          type: 'string',
          humanReadable: 'Navn',
          meta: {
            group: 'Kunde',
            isMain: true,
          },
          inSearchSelect: true,
        },
        updateTime: {
          type: 'string',
          humanReadable: 'Sist oppdatert',
          meta: {
            group: 'Kunde',
            hideFromTable: true,
          },
        },
      },
    },

    connectedAccount: {
      type: 'object',
      humanReadable: 'Tilknyttet konto',
      meta: {
        group: 'Selger',
      },
      values: {
        connectedAccountID: {
          type: 'string',
          humanReadable: 'Connected Account ID',
          meta: {
            group: 'Selger',
            hideFromTable: true,
          },
        },
        name: {
          type: 'string',
          humanReadable: 'Alias navn',
          meta: {
            group: 'Selger',
            isMain: true,
          },
          inSearchSelect: true,
        },
        businessName: {
          type: 'string',
          humanReadable: 'Bedriftsnavn',
          meta: {
            group: 'Selger',
          },
        },
        countryCode: {
          type: 'string',
          humanReadable: 'Landskode',
          meta: {
            group: 'Selger',
          },
        },
        business_TaxID: {
          type: 'string',
          humanReadable: 'Organisasjonsnummer',
          meta: {
            group: 'Selger',
          },
        },
      },
    },
  },
}

// export const PayoutsEmbedFullSchema: JSSchema<PayoutsEmbed.Full, typeof PayoutsEmbedGroups> = {
//   ...(PayoutsEmbedListSchema as JSSchema<PayoutsEmbed.Full, typeof PayoutsEmbedGroups>),
//   title: 'Payout',
// }
